import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, type NavigateOptions } from 'react-router-dom';

/** Duración del fade entre pantallas del cuestionario (ms). Debe coincidir con la
 *  clase `duration-[...]` de las páginas para que el navigate ocurra al terminar. */
export const STEP_FADE_MS = 250;

/**
 * Transición de fade in/out entre los pasos del "Account setup" (ADR-715).
 *
 * - `visible`: togglea la opacidad del contenedor de la pantalla. Arranca en `false`
 *   y pasa a `true` en el próximo frame → **fade-in** al montar.
 * - `navigateWithFade(to)`: hace **fade-out** (opacidad → 0) y recién al terminar la
 *   transición navega; la próxima pantalla entra con su propio fade-in. Acepta una
 *   ruta (`string`) o un delta de historial (`number`, ej. `-1` para volver).
 *
 * Como ambas pantallas están sobre fondo blanco, el resultado es un fundido suave
 * (sin corte brusco) sin necesidad de librerías de animación.
 */
export function useStepFade() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const timer = useRef<number>();

  // Fade-in al montar: pintamos con opacity-0 y en el próximo frame vamos a 1.
  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => {
      cancelAnimationFrame(raf);
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  const navigateWithFade = useCallback(
    // `options` (state/replace) se pasa tal cual a `navigate`: lo usa la pantalla de
    // generación para mandarle a resultados las imágenes reales ya precargadas.
    (to: string | number, options?: NavigateOptions) => {
      setVisible(false); // dispara el fade-out
      timer.current = window.setTimeout(() => {
        if (typeof to === 'number') navigate(to);
        else navigate(to, options);
      }, STEP_FADE_MS);
    },
    [navigate],
  );

  return { visible, navigate, navigateWithFade };
}

export default useStepFade;
